import { useEffect, useState, type RefObject } from 'react'
import styles from './CastButton.module.css'

interface Props {
  videoRef: RefObject<HTMLVideoElement | null>
  onCast?: (casting: boolean) => void
}

export function CastButton({ videoRef, onCast }: Props) {
  const [available, setAvailable] = useState(false)
  const [casting, setCasting] = useState(false)

  useEffect(() => {
    const video = videoRef.current as HTMLVideoElement & {
      remote?: RemotePlayback
      webkitShowPlaybackTargetPicker?: () => void
    } | null
    if (!video) return

    const remote = video.remote
    if (remote && typeof remote.watchAvailability === 'function') {
      let cancelled = false
      void Promise.resolve(remote.watchAvailability((ok) => {
        if (!cancelled) setAvailable(!!ok)
      })).catch(() => {
        if (!cancelled) setAvailable(true)
      })
      const onConnecting = () => {
        setCasting(true)
        onCast?.(true)
      }
      const onDisconnect = () => {
        setCasting(false)
        onCast?.(false)
      }
      remote.addEventListener('connecting', onConnecting)
      remote.addEventListener('connect', onConnecting)
      remote.addEventListener('disconnect', onDisconnect)
      return () => {
        cancelled = true
        try {
          remote.cancelWatchAvailability?.()
        } catch {
          /* ignore */
        }
        remote.removeEventListener('connecting', onConnecting)
        remote.removeEventListener('connect', onConnecting)
        remote.removeEventListener('disconnect', onDisconnect)
      }
    }

    // Safari AirPlay picker availability — show button on Apple / when API exists
    if (typeof video.webkitShowPlaybackTargetPicker === 'function') {
      setAvailable(true)
    }
  }, [videoRef, onCast])

  if (!available) return null

  const onClick = async () => {
    const video = videoRef.current as HTMLVideoElement & {
      remote?: RemotePlayback
      webkitShowPlaybackTargetPicker?: () => void
    } | null
    if (!video) return
    try {
      if (video.remote && typeof video.remote.prompt === 'function') {
        await video.remote.prompt()
        return
      }
      video.webkitShowPlaybackTargetPicker?.()
    } catch {
      /* user cancelled */
    }
  }

  return (
    <button
      type="button"
      className={`${styles.btn} ${casting ? styles.active : ''}`}
      onClick={() => void onClick()}
      aria-label="Cast"
      title="Cast"
    >
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
        <path
          fill="currentColor"
          d="M1 18v3h3c0-1.66-1.34-3-3-3zm0-4v2c2.76 0 5 2.24 5 5h2c0-3.87-3.13-7-7-7zm0-4v2c4.97 0 9 4.03 9 9h2c0-6.08-4.93-11-11-11zm20-7H3c-1.1 0-2 .9-2 2v3h2V5h18v14h-7v2h7c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"
        />
      </svg>
    </button>
  )
}
